/**
 * Staff Dashboard v2 — consumes GET /api/staff/dashboard.
 *
 * Renders inside the StaffPortalShell when both `staff_portal_v2_enabled`
 * and `staff_dashboard_v2_enabled` are ON. When either is OFF this
 * component is not routed to (see App.js wrapper); legacy StaffDashboard.js
 * remains the fallback.
 *
 * Contract:
 *   • Single aggregate request on mount.
 *   • Partial failure handled via `sections_ok` / `sections_failed`.
 *   • Full-error state shows Retry.
 *   • Empty states are section-specific, not a generic "No data".
 *   • Every card deep-links to an existing production route.
 *   • No demo data. No fake counts. No fake names.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { clockInWindow, clockInClosedText } from '@/lib/clockInWindow';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useStaffNotifications } from '@/contexts/StaffNotificationsContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Clock, Calendar, ClipboardList, Users, FileText, AlertTriangle,
  Bell, Megaphone, RefreshCw, ChevronRight, CheckCircle2, PlayCircle,
  ShieldAlert,
} from 'lucide-react';

function Skeleton({ className = '' }) {
  return <div className={`animate-pulse bg-muted rounded ${className}`} />;
}

function SectionErrorInline({ label, onRetry }) {
  return (
    <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid={`section-error-${label}`}>
      <AlertTriangle className="h-3.5 w-3.5" />
      <span className="flex-1">Couldn&apos;t load {label}.</span>
      {onRetry && (
        <button onClick={onRetry} className="underline underline-offset-2 font-medium">Retry</button>
      )}
    </div>
  );
}

function CurrentShiftCard({ shift, next }) {
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNowMs(Date.now()), 30000); return () => clearInterval(t); }, []);
  if (!shift && !next) {
    return (
      <Card data-testid="dash-current-shift-empty">
        <CardContent className="py-6 text-center text-muted-foreground text-sm">
          No shift currently in progress and no upcoming shift assigned.
        </CardContent>
      </Card>
    );
  }
  const display = shift || next;
  const isActive = !!shift;
  const isAssignedNext = !shift && !!next;
  // Clock-in window: the next shift's Clock In appears from 2 h before its start (until it ends)
  const closedText = !isActive ? clockInClosedText(clockInWindow(display, nowMs)) : '';
  return (
    <Card className="border-l-4 border-l-primary" data-testid="dash-current-shift">
      <CardHeader className="pb-2 flex flex-row items-start justify-between gap-2 space-y-0">
        <div>
          <div className="flex items-center gap-2">
            <CardTitle className="text-base">{isActive ? 'Current shift' : 'Next shift'}</CardTitle>
            {isActive && <Badge className="bg-emerald-500 hover:bg-emerald-600" data-testid="dash-current-shift-active-badge">Active</Badge>}
            {isAssignedNext && <Badge variant="outline">Upcoming</Badge>}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {display.date} · {display.start_time} – {display.end_time}
          </p>
        </div>
        {closedText ? (
          <span className="text-xs text-muted-foreground text-right max-w-[220px]" data-testid="dash-clock-closed">{closedText}</span>
        ) : (
          <Button asChild size="sm" data-testid="dash-clock-cta">
            <Link to="/my/clock">
              {isActive ? (<><CheckCircle2 className="h-4 w-4 mr-1.5" /> Clock Out</>) : (<><PlayCircle className="h-4 w-4 mr-1.5" /> Clock In</>)}
            </Link>
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-2 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
        <div>
          <div className="text-xs text-muted-foreground">Participant</div>
          <div className="font-medium">{display.participant_name || display.participant_id || '—'}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Location</div>
          <div className="font-medium truncate">{display.location || '—'}</div>
        </div>
        <div className="flex items-end justify-start sm:justify-end">
          <Button asChild variant="ghost" size="sm" data-testid="dash-shift-details-link">
            <Link to={`/portal/shift/${display.id}`}>Shift details <ChevronRight className="h-3.5 w-3.5 ml-1" /></Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function StatTile({ label, value, to, icon: Icon, testId }) {
  const content = (
    <Card className="hover:border-primary/40 hover:shadow-sm transition-colors h-full" data-testid={testId}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <Icon className="h-4.5 w-4.5" />
          </div>
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground uppercase tracking-wide truncate">{label}</div>
            <div className="text-xl font-bold tabular-nums">{value == null ? '—' : value}</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
  return to ? <Link to={to} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg">{content}</Link> : content;
}

function AlertsPanel({ documentation, compliance, failedSection, onRetry }) {
  const items = [
    ...(documentation || []).map((d) => ({ ...d, _kind: 'doc' })),
    ...(compliance || []).map((d) => ({ ...d, _kind: 'compliance' })),
  ];
  return (
    <Card data-testid="dash-alerts">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-red-600" /> Critical alerts
        </CardTitle>
        {items.length > 0 && <Badge variant="destructive" data-testid="dash-alerts-count">{items.length}</Badge>}
      </CardHeader>
      <CardContent className="space-y-2">
        {failedSection && <SectionErrorInline label={failedSection} onRetry={onRetry} />}
        {items.length === 0 && !failedSection && (
          <div className="text-sm text-muted-foreground py-2" data-testid="dash-alerts-empty">No critical alerts.</div>
        )}
        {items.slice(0, 6).map((a) => (
          <div key={a.id || `${a._kind}-${a.title}`} className="flex items-start gap-2 text-sm" data-testid={`alert-${a._kind}-${a.id || 'x'}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 ${a._kind === 'doc' ? 'bg-orange-500' : 'bg-red-500'}`} aria-hidden="true" />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{a.title || a.message || 'Alert'}</div>
              {a.due_date && <div className="text-xs text-muted-foreground">Due {a.due_date}</div>}
            </div>
            <Badge variant="outline" className="text-[10px]">{a._kind === 'doc' ? 'DOC' : 'COMPLIANCE'}</Badge>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function TasksPanel({ tasks, failedSection, onRetry, viewAllTo, overdueCount }) {
  return (
    <Card data-testid="dash-tasks">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          <ClipboardList className="h-4 w-4" /> Outstanding tasks
          {overdueCount > 0 && <Badge variant="destructive" className="text-[10px]" data-testid="dash-tasks-overdue">{overdueCount} overdue</Badge>}
        </CardTitle>
        {viewAllTo && <Link to={viewAllTo} className="text-xs text-primary underline focus-visible:ring-2 focus-visible:ring-primary rounded" data-testid="dash-tasks-view-all">View all</Link>}
      </CardHeader>
      <CardContent className="space-y-2">
        {failedSection && <SectionErrorInline label={failedSection} onRetry={onRetry} />}
        {!failedSection && (!tasks || tasks.length === 0) && (
          <div className="text-sm text-muted-foreground py-2" data-testid="dash-tasks-empty">No outstanding tasks.</div>
        )}
        {(tasks || []).slice(0, 5).map((t) => (
          <div key={t.id} className="flex items-start gap-2 text-sm" data-testid={`task-${t.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{t.title}</div>
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                {t.is_overdue && <Badge variant="destructive" className="text-[10px]" data-testid={`dash-task-overdue-${t.id}`}>OVERDUE</Badge>}
                {t.due_date && <span>Due {t.due_date}</span>}
                {t.priority && <Badge variant="outline" className="text-[10px]">{t.priority}</Badge>}
                {t.status && <span>· {t.status.replace('_', ' ')}</span>}
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function AppointmentsPanel({ appointments, failedSection, onRetry }) {
  return (
    <Card data-testid="dash-appointments">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4" /> Upcoming appointments</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {failedSection && <SectionErrorInline label={failedSection} onRetry={onRetry} />}
        {!failedSection && (!appointments || appointments.length === 0) && (
          <div className="text-sm text-muted-foreground py-2" data-testid="dash-appointments-empty">No upcoming appointments.</div>
        )}
        {(appointments || []).slice(0, 5).map((a) => (
          <div key={a.id} className="flex items-start gap-2 text-sm" data-testid={`appt-${a.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{a.title || a.subject || 'Appointment'}</div>
              <div className="text-xs text-muted-foreground">{a.date} · {a.start_time}</div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function AnnouncementsPanel({ announcements, failedSection, onRetry }) {
  return (
    <Card data-testid="dash-announcements">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2"><Megaphone className="h-4 w-4" /> Announcements</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {failedSection && <SectionErrorInline label={failedSection} onRetry={onRetry} />}
        {!failedSection && (!announcements || announcements.length === 0) && (
          <div className="text-sm text-muted-foreground py-2" data-testid="dash-announcements-empty">No announcements.</div>
        )}
        {(announcements || []).map((a) => (
          <div key={a.id} className="text-sm border-l-2 border-border pl-3" data-testid={`ann-${a.id}`}>
            <div className="font-medium">{a.title}</div>
            {a.summary && <div className="text-xs text-muted-foreground mt-0.5">{a.summary}</div>}
            {a.published_at && <div className="text-[11px] text-muted-foreground mt-0.5">{a.published_at}</div>}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function NotificationsPanel({ notifications, failedSection, onRetry }) {
  const items = notifications?.items || [];
  const unread = notifications?.unread_count || 0;
  return (
    <Card data-testid="dash-notifications">
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          <Bell className="h-4 w-4" /> Notifications
          {unread > 0 && <Badge variant="destructive" data-testid="dash-notifications-unread">{unread}</Badge>}
        </CardTitle>
        <Button asChild variant="ghost" size="sm">
          <Link to="/notifications">View all</Link>
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {failedSection && <SectionErrorInline label={failedSection} onRetry={onRetry} />}
        {!failedSection && items.length === 0 && (
          <div className="text-sm text-muted-foreground py-2" data-testid="dash-notifications-empty">No unread notifications.</div>
        )}
        {items.slice(0, 5).map((n) => (
          <Link
            key={n.id}
            to={n.deep_link || '/notifications'}
            className={`block text-sm py-1 ${n.read_at ? 'text-muted-foreground' : 'text-foreground font-medium'}`}
            data-testid={`notif-${n.id}`}
          >
            <div className="truncate">{n.title}</div>
            {n.preview && <div className="text-xs text-muted-foreground truncate">{n.preview}</div>}
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}

export default function StaffDashboardV2() {
  const { user } = useAuth();
  const { setUnreadCount } = useStaffNotifications();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await api.get('/staff/dashboard');
      const payload = res.data?.data || res.data;
      setData(payload);
      // Seed the shared unread count so the shell badge matches. Only
      // update when the backend value changes to avoid render loops.
      const unread = payload?.notifications?.unread_count;
      if (typeof unread === 'number') setUnreadCount(unread);
    } catch (e) {
      setError(e?.response?.data?.error?.message || e?.message || 'Unable to load dashboard');
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [setUnreadCount]);

  useEffect(() => { load(); }, [load]);

  const refresh = () => { setRefreshing(true); load(); };
  const failedSet = new Set((data?.sections_failed) || []);
  const firstName = user?.full_name?.split(' ')[0] || 'there';

  return (
    <div className="space-y-4 md:space-y-6" data-testid="staff-dashboard-v2">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-xl md:text-2xl font-bold tracking-tight truncate">Welcome back, {firstName}</h2>
          <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
            {new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} disabled={loading || refreshing} data-testid="staff-dashboard-v2-refresh">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Loading */}
      {loading && !data && (
        <div className="space-y-4" aria-live="polite" aria-busy="true" data-testid="staff-dashboard-v2-loading">
          <Skeleton className="h-32 w-full" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Skeleton className="h-20" /><Skeleton className="h-20" /><Skeleton className="h-20" /><Skeleton className="h-20" />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Skeleton className="h-48" /><Skeleton className="h-48" />
          </div>
        </div>
      )}

      {/* Full error */}
      {!loading && error && (
        <Card className="border-red-200 bg-red-50" data-testid="staff-dashboard-v2-error">
          <CardContent className="py-6 text-center space-y-3">
            <AlertTriangle className="h-8 w-8 text-red-600 mx-auto" />
            <div className="font-medium text-red-800">Couldn&apos;t load your dashboard</div>
            <div className="text-sm text-red-700">{error}</div>
            <Button onClick={refresh} data-testid="staff-dashboard-v2-retry">Retry</Button>
          </CardContent>
        </Card>
      )}

      {/* Data */}
      {!loading && data && (
        <>
          {/* 1. Current shift + clock */}
          {failedSet.has('shifts')
            ? <Card data-testid="dash-shifts-failed"><CardContent className="py-4"><SectionErrorInline label="shifts" onRetry={refresh} /></CardContent></Card>
            : <CurrentShiftCard shift={data.current_shift} next={data.next_shift} />
          }

          {/* 2. Critical alerts */}
          <AlertsPanel
            documentation={data.documentation_alerts}
            compliance={data.compliance_alerts}
            clinical={data.clinical_alerts}
            failedSection={failedSet.has('documentation_alerts') ? 'documentation_alerts' : (failedSet.has('compliance_alerts') ? 'compliance_alerts' : null)}
            onRetry={refresh}
          />

          {/* 3. Operational statistics — approved KPI set */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dash-stats">
            <StatTile testId="stat-today" label="Today&apos;s shifts" value={data.today_shift_count} to="/staff/roster" icon={Calendar} />
            <StatTile testId="stat-tasks" label="Open tasks" value={data.outstanding_task_count} to={data.tasks_source === 'participant_tasks' ? '/staff/tasks' : '/my/work'} icon={ClipboardList} />
            <StatTile testId="stat-messages" label="Unread messages" value={data.messaging?.available ? data.messaging.unread_count : '—'} icon={FileText} />
            <StatTile
              testId="stat-alerts" label="Alerts" to="/notifications" icon={ShieldAlert}
              value={(data.documentation_alerts?.length || 0) + (data.compliance_alerts?.length || 0) + (data.clinical_alerts?.length || 0)}
            />
            <StatTile testId="stat-timesheets" label="Open timesheets" value={data.open_timesheet_count} to="/timesheets/my-v2" icon={FileText} />
            <StatTile testId="stat-participants" label="Assigned participants" value={data.assigned_participant_count} icon={Users} />
          </div>

          {/* Today's shifts panel */}
          <Card data-testid="dash-today-shifts">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Calendar className="h-4 w-4 text-primary" /> Today&apos;s shifts</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {(data.today_shifts || []).length === 0 && <div className="text-sm text-muted-foreground py-1" data-testid="dash-today-shifts-empty">No shifts scheduled today.</div>}
              {(data.today_shifts || []).map((s) => (
                <Link key={s.id} to={`/portal/shift/${s.id}`} className="flex items-center gap-3 text-sm py-1 hover:text-primary" data-testid={`today-shift-${s.id}`}>
                  <span className="tabular-nums font-medium">{s.start_time}–{s.end_time}</span>
                  <span className="truncate text-muted-foreground">{s.participant_name || s.location || 'Shift'}</span>
                  {s.check_in_time && !s.check_out_time && <Badge className="bg-emerald-500 ml-auto">Clocked in</Badge>}
                </Link>
              ))}
            </CardContent>
          </Card>

          {/* 4-8. Panels */}
          <div className="grid gap-4 md:grid-cols-2">
            <TasksPanel tasks={data.tasks} failedSection={failedSet.has('tasks') ? 'tasks' : null} onRetry={refresh}
                        viewAllTo={data.tasks_source === 'participant_tasks' ? '/staff/tasks' : null}
                        overdueCount={data.overdue_task_count || 0} />
            <AppointmentsPanel appointments={data.appointments} failedSection={failedSet.has('appointments') ? 'appointments' : null} onRetry={refresh} />
            <AnnouncementsPanel announcements={data.announcements} failedSection={failedSet.has('announcements') ? 'announcements' : null} onRetry={refresh} />
            <NotificationsPanel notifications={data.notifications} failedSection={failedSet.has('notifications') ? 'notifications' : null} onRetry={refresh} />
            {/* Internal messaging / team chat — honest unavailable state (no backend yet) */}
            <Card data-testid="dash-messaging">
              <CardHeader className="pb-2"><CardTitle className="text-base">Messages &amp; team chat</CardTitle></CardHeader>
              <CardContent>
                <div className="text-sm text-muted-foreground py-2" data-testid="dash-messaging-unavailable">
                  {data.messaging?.reason || 'Internal messaging and team chat are not yet available.'}
                </div>
              </CardContent>
            </Card>
          </div>

          {data.generated_at && (
            <div className="text-[11px] text-muted-foreground text-right">
              Updated {new Date(data.generated_at).toLocaleTimeString()}
            </div>
          )}
        </>
      )}
    </div>
  );
}
